/* ==========================================================================
   Global Variables & reCAPTCHA Callback
   ========================================================================== */

/* Глобальная переменная для отслеживания, какая форма инициировала reCAPTCHA.
  Это необходимо, так как callback от Google глобальный.
*/

window.currentSubmittingForm = null;
window.quoteFormSubmittedSuccessfully = false;

function loadScriptOnce(src, id) {
  let existing = id ? document.getElementById(id) : null;
  if (existing && existing.dataset.failed === 'true') {
    existing.remove();
    existing = null;
  }
  if (existing) {
    return existing.dataset.loaded === 'true'
      ? Promise.resolve(existing)
      : new Promise((resolve, reject) => {
        existing.addEventListener('load', () => resolve(existing), { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    if (id) script.id = id;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve(script);
    }, { once: true });
    script.addEventListener('error', (error) => {
      script.dataset.failed = 'true';
      reject(error);
    }, { once: true });
    document.head.appendChild(script);
  });
}

function loadStylesheetOnce(href, id) {
  if (id && document.getElementById(id)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  if (id) link.id = id;
  document.head.appendChild(link);
}

let recaptchaApiPromise = null;
const RECAPTCHA_API_TIMEOUT = 8000;
const RECAPTCHA_VERIFICATION_TIMEOUT = 45000;
const RECAPTCHA_WIDGET_SETTLE_DELAY = 700;
const RECAPTCHA_RETRY_DELAY = 900;
const RECAPTCHA_MAX_RETRIES = 2;
const FORM_VALUE_SETTLE_DELAY = 200;
const FORM_VALUE_RETRY_DELAY = 300;
const RECAPTCHA_API_URLS = [
  'https://www.recaptcha.net/recaptcha/api.js?render=explicit',
  'https://www.google.com/recaptcha/api.js?render=explicit',
];

function withTimeout(promise, timeout, message) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(message)), timeout);

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

function waitForRecaptchaApi() {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const checkApi = () => {
      if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
        resolve(window.grecaptcha);
        return;
      }

      if (Date.now() - startedAt >= RECAPTCHA_API_TIMEOUT) {
        reject(new Error('reCAPTCHA API did not become ready'));
        return;
      }

      window.setTimeout(checkApi, 50);
    };
    checkApi();
  });
}

function loadRecaptchaApi() {
  if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
    return Promise.resolve(window.grecaptcha);
  }

  if (!recaptchaApiPromise) {
    recaptchaApiPromise = (async () => {
      let lastError = null;

      for (let index = 0; index < RECAPTCHA_API_URLS.length; index += 1) {
        const scriptId = `recaptcha-api-script-${index}`;

        try {
          await withTimeout(
            loadScriptOnce(RECAPTCHA_API_URLS[index], scriptId),
            RECAPTCHA_API_TIMEOUT,
            'reCAPTCHA script loading timed out'
          );
          return await waitForRecaptchaApi();
        } catch (error) {
          lastError = error;
          const failedScript = document.getElementById(scriptId);
          if (failedScript) failedScript.remove();
        }
      }

      throw lastError || new Error('reCAPTCHA API is unavailable');
    })().catch((error) => {
      recaptchaApiPromise = null;
      throw error;
    });
  }

  return recaptchaApiPromise;
}

function trackAnalyticsEvent(eventName, params = {}) {
  if (!window.obCookieConsent || !window.obCookieConsent.hasConsent('analytics')) return;

  const eventPayload = {
    page_location: window.location.href,
    page_path: window.location.pathname,
    ...params,
  };

  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventPayload);
  }
}

function getQuoteFormSource(trigger) {
  if (trigger && trigger.closest('header')) return 'header';
  if (trigger && trigger.closest('footer')) return 'footer';

  const path = window.location.pathname;
  if (/^\/services\/[^/]+\/?$/.test(path)) return 'service_page';
  if (/^\/services\/?$/.test(path)) return 'services_hub';
  if (/^\/projects(?:\/|$)/.test(path)) return 'project_page';
  if (path === '/') return 'homepage';
  return 'other_page';
}

window.onSubmit = function(token) {
  const form = window.currentSubmittingForm;
  if (!form) return;

  if (typeof form._clearQuoteVerificationTimer === 'function') {
    form._clearQuoteVerificationTimer();
  }

  if (!token) {
    form.dispatchEvent(new CustomEvent('recaptcha-error', { bubbles: true }));
    return;
  }

  let tokenEl = form.querySelector('[name="g-recaptcha-response"]');
  if (!tokenEl) {
    tokenEl = document.createElement('textarea');
    tokenEl.name = 'g-recaptcha-response';
    tokenEl.style.display = 'none';
    form.appendChild(tokenEl);
  }
  tokenEl.value = token;

  form.dispatchEvent(new CustomEvent('recaptcha-verified', { bubbles: true }));
};

document.addEventListener('DOMContentLoaded', () => {

  /* ==========================================================================
     UI / Layout Helpers (Scroll, Header, Navigation)
     ========================================================================== */

  const yearEl = document.getElementById('year');
  if (yearEl) yearEl.textContent = new Date().getFullYear();

  const scrollBtn = document.getElementById('scrollTopBtn');
  if (scrollBtn) {
    window.addEventListener('scroll', () => {
      scrollBtn.style.display = window.pageYOffset > 200 ? 'block' : 'none';
    });
    scrollBtn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // Smooth anchors with dynamic fixed header offset
  const EXTRA_HEADER_PAD = 0; // extra pixels to ensure titles clear the header

  // Делаем функцию доступной глобально (или внутри модуля), так как она нужна при валидации
  const getHeaderOffset = () => {
    const candidates = Array.from(document.querySelectorAll('header'));
    let offset = 0;

    candidates.forEach(el => {
      const cs = getComputedStyle(el);
      const pos = cs.position;
      if (pos !== 'fixed' && pos !== 'sticky') return;

      const rect = el.getBoundingClientRect();
      const nearTop = rect.top <= 1;
      if (nearTop || pos === 'sticky') {
        offset += rect.height;
      }
    });

    if (offset === 0) {
      const h = document.querySelector('header');
      if (h) offset = h.getBoundingClientRect().height;
    }

    return Math.ceil(offset + EXTRA_HEADER_PAD);
  };

  const debounceGlobal = (fn, t = 150) => { let id; return (...a) => { clearTimeout(id); id = setTimeout(() => fn(...a), t); }; };

  function scrollToWithOffset(target, behavior = 'smooth') {
    if (!target) return;
    const firstOffset = getHeaderOffset();
    const firstY = target.getBoundingClientRect().top + window.pageYOffset - firstOffset;
    window.scrollTo({ top: Math.max(0, firstY), behavior });

    setTimeout(() => {
      const secondOffset = getHeaderOffset();
      const secondY = target.getBoundingClientRect().top + window.pageYOffset - secondOffset;
      window.scrollTo({ top: Math.max(0, secondY), behavior: 'auto' });
    }, 350);
  }

  function updateScrollMargins() {
    const off = getHeaderOffset();
    document.documentElement.style.setProperty('--header-offset', `${off}px`);
    const ids = Array.from(document.querySelectorAll('a[href^="#"]'))
      .map(a => a.getAttribute('href'))
      .filter(href => href && href.length > 1)
      .map(href => href.trim());
    const unique = Array.from(new Set(ids));
    unique.forEach(sel => {
      const el = document.querySelector(sel);
      if (el) el.style.scrollMarginTop = `${off + 1}px`;
    });
  }

  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', e => {
      const href = anchor.getAttribute('href');
      if (!href || href === '#') return;
      if (anchor.classList.contains('process__nav-item')) return;
      if (anchor.closest('header, .site-header, .navbar, [data-fixed-header]')) return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        scrollToWithOffset(target, 'smooth');
        if (history.replaceState) {
          history.replaceState(null, '', location.pathname + location.search);
        }
      }
    });
  });

  document.querySelectorAll('header a[href*="#"], .site-header a[href*="#"], .navbar a[href*="#"]').forEach(anchor => {
    anchor.addEventListener('click', e => {
      const rawHref = anchor.getAttribute('href') || '';
      let url;
      try { url = new URL(rawHref, location.href); } catch { return; }
      const { hash, pathname, origin } = url;
      if (!hash) return;
      if (origin !== location.origin) return;
      if (pathname !== location.pathname) return;

      const target = document.querySelector(hash);
      if (!target) return;

      e.preventDefault();
      scrollToWithOffset(target, 'smooth');
      if (history.replaceState) {
        history.replaceState(null, '', location.pathname + location.search);
      }
    });
  });

  if (location.hash) {
    const initialTarget = document.querySelector(location.hash);
    if (initialTarget) {
      setTimeout(() => {
        scrollToWithOffset(initialTarget, 'auto');
        if (history.replaceState) {
          history.replaceState(null, '', location.pathname + location.search);
        }
      }, 0);
    }
  }

  updateScrollMargins();
  window.addEventListener('resize', debounceGlobal(updateScrollMargins, 150));
  window.addEventListener('orientationchange', updateScrollMargins);

  // Navbar logic
  const BREAKPOINT = 992;
  const menu = document.getElementById('navbarMenu');
  const toggler = document.querySelector('.mobile-nav-trigger__btn');
  if (menu && window.bootstrap) {
    const isDesktop = () => window.innerWidth >= BREAKPOINT;
    const inst = () => bootstrap.Collapse.getOrCreateInstance(menu, { toggle: false });

    menu.querySelectorAll('.nav-link').forEach(link => {
      link.addEventListener('click', () => {
        if (!isDesktop()) {
          inst().hide();
          if (toggler) toggler.setAttribute('aria-expanded', 'false');
        }
      });
    });

    const syncToViewport = () => {
      if (isDesktop()) {
        inst().show();
        menu.classList.remove('collapsing');
        menu.style.height = '';
        if (toggler) toggler.setAttribute('aria-expanded', 'true');
      } else {
        inst().hide();
        if (toggler) toggler.setAttribute('aria-expanded', 'false');
      }
    };
    syncToViewport();
    window.addEventListener('resize', debounceGlobal(syncToViewport, 150));
  }

  // Keep the initial hero stable long enough to become LCP, then resume carousel UX.
  const heroCarouselEl = document.getElementById('heroSlider');
  if (heroCarouselEl && window.bootstrap) {
    const heroCarousel = bootstrap.Carousel.getOrCreateInstance(heroCarouselEl, {
      interval: 3500,
      ride: false,
      pause: 'hover',
    });
    let heroStarted = false;

    const startHeroCarousel = () => {
      if (heroStarted || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      heroStarted = true;
      heroCarousel.cycle();
    };

    const scheduleHeroCarousel = () => {
      const delay = Number(heroCarouselEl.dataset.autoplayDelay) || 15000;
      window.setTimeout(startHeroCarousel, delay);
    };

    if (document.readyState === 'complete') {
      scheduleHeroCarousel();
    } else {
      window.addEventListener('load', scheduleHeroCarousel, { once: true });
    }

    ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => {
      heroCarouselEl.addEventListener(eventName, startHeroCarousel, { once: true, passive: true });
    });
  }

  const loadVideo = (video) => {
    if (!video || video.dataset.loaded === 'true') return;
    const src = video.dataset.src;
    if (!src) return;
    video.src = src;
    video.dataset.loaded = 'true';
    video.load();
  };

  const playVideo = (video) => {
    loadVideo(video);
    if (video) video.play().catch(() => {});
  };

  const serviceCarouselEl = document.querySelector('[data-lazy-carousel]');
  if (serviceCarouselEl && window.bootstrap) {
    const paginationItems = Array.from(document.querySelectorAll('.services-pagination .pagination-item'));
    const serviceCarousel = bootstrap.Carousel.getOrCreateInstance(serviceCarouselEl, {
      interval: 3500,
      ride: false,
      pause: 'hover',
    });
    let serviceCarouselStarted = false;

    const getVideoAt = (index) => {
      const slides = serviceCarouselEl.querySelectorAll('.carousel-item');
      return slides[index] ? slides[index].querySelector('video[data-src]') : null;
    };

    const startServiceCarousel = () => {
      if (serviceCarouselStarted) return;
      serviceCarouselStarted = true;
      const activeIndex = Math.max(
        0,
        Array.from(serviceCarouselEl.querySelectorAll('.carousel-item')).findIndex((item) => item.classList.contains('active'))
      );
      playVideo(getVideoAt(activeIndex));
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        serviceCarousel.cycle();
      }
    };

    paginationItems.forEach((item) => {
      item.addEventListener('click', () => {
        paginationItems.forEach((entry) => entry.classList.remove('active'));
        item.classList.add('active');
      });
    });

    serviceCarouselEl.addEventListener('slide.bs.carousel', (event) => {
      playVideo(getVideoAt(event.to));
    });
    serviceCarouselEl.addEventListener('slid.bs.carousel', (event) => {
      serviceCarouselEl.querySelectorAll('video').forEach((video, index) => {
        if (index !== event.to) video.pause();
      });
      paginationItems.forEach((entry, index) => {
        entry.classList.toggle('active', index === event.to);
      });
    });

    if ('IntersectionObserver' in window) {
      const carouselObserver = new IntersectionObserver((entries, observer) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          startServiceCarousel();
          observer.disconnect();
        }
      }, { rootMargin: '400px 0px' });
      carouselObserver.observe(serviceCarouselEl);
    } else {
      startServiceCarousel();
    }
  }

  document.querySelectorAll('[data-process-slider]').forEach((processSlider) => {
    const carouselEl = processSlider.querySelector('[data-process-carousel]');
    if (!carouselEl || !window.bootstrap) return;

    const controls = Array.from(processSlider.querySelectorAll('[data-process-slide-to]'));
    const interval = Number(processSlider.dataset.processInterval) || 6000;
    const processCarousel = bootstrap.Carousel.getOrCreateInstance(carouselEl, {
      interval,
      ride: false,
      pause: 'hover',
      touch: true,
      wrap: true,
    });
    let processCarouselStarted = false;
    let pendingProcessIndex = null;
    let processResumeTimer = null;

    const setActiveProcessStep = (activeIndex) => {
      controls.forEach((control, index) => {
        const isActive = index === activeIndex;
        control.classList.toggle('active', isActive);
        if (isActive) {
          control.setAttribute('aria-current', 'step');
        } else {
          control.removeAttribute('aria-current');
        }
      });
    };

    controls.forEach((control) => {
      control.addEventListener('click', (event) => {
        event.preventDefault();
        const targetIndex = Number(control.dataset.processSlideTo);
        if (Number.isNaN(targetIndex)) return;

        if (processResumeTimer) window.clearTimeout(processResumeTimer);
        const activeIndex = Array.from(carouselEl.querySelectorAll('.carousel-item'))
          .findIndex((item) => item.classList.contains('active'));
        const isAnimating = !!carouselEl.querySelector(
          '.carousel-item-next, .carousel-item-prev, .carousel-item-start, .carousel-item-end'
        );

        if (activeIndex === targetIndex && !isAnimating) {
          pendingProcessIndex = null;
          setActiveProcessStep(targetIndex);
          if (processCarouselStarted && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            processResumeTimer = window.setTimeout(() => processCarousel.cycle(), 350);
          }
          return;
        }

        pendingProcessIndex = targetIndex;
        processCarousel.pause();
        setActiveProcessStep(targetIndex);
        processCarousel.to(targetIndex);
      });
    });

    carouselEl.addEventListener('slide.bs.carousel', (event) => {
      if (pendingProcessIndex === null) setActiveProcessStep(event.to);
    });

    carouselEl.addEventListener('slid.bs.carousel', (event) => {
      if (pendingProcessIndex !== null && event.to !== pendingProcessIndex) {
        window.requestAnimationFrame(() => processCarousel.to(pendingProcessIndex));
        return;
      }

      const selectedIndex = pendingProcessIndex === null ? event.to : pendingProcessIndex;
      pendingProcessIndex = null;
      setActiveProcessStep(selectedIndex);

      if (processCarouselStarted && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        processResumeTimer = window.setTimeout(() => processCarousel.cycle(), 350);
      }
    });

    const startProcessCarousel = () => {
      if (processCarouselStarted || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      processCarouselStarted = true;
      processCarousel.cycle();
    };

    if ('IntersectionObserver' in window) {
      const processCarouselObserver = new IntersectionObserver((entries, observer) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          startProcessCarousel();
          observer.disconnect();
        }
      }, { rootMargin: '250px 0px' });
      processCarouselObserver.observe(processSlider);
    } else {
      startProcessCarousel();
    }
  });

  document.querySelectorAll('video[data-src]').forEach((video) => {
    if (video.closest('[data-lazy-carousel]')) return;

    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            playVideo(video);
          } else if (video.dataset.loaded === 'true') {
            video.pause();
          }
        });
      }, { rootMargin: '300px 0px' });
      videoObserver.observe(video);
    } else {
      playVideo(video);
    }
  });

  /* ==========================================================================
     Analytics: quote modal / form interactions
     ========================================================================== */

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-bs-target="#quoteModal"]');
    if (!openBtn) return;

    const quoteForm = document.getElementById('quoteForm');
    if (quoteForm) {
      quoteForm.dataset.formSource = getQuoteFormSource(openBtn);
      if (typeof quoteForm._prepareQuoteRecaptcha === 'function') {
        quoteForm._prepareQuoteRecaptcha().catch(() => {});
      }
    }

    trackAnalyticsEvent('quote_modal_open', {
      modal_id: 'quoteModal',
      trigger_text: (openBtn.textContent || '').trim(),
    });
  });

  document.addEventListener('click', (e) => {
    const submitBtn = e.target.closest('#quoteModal [data-recaptcha-submit], #quoteModal button[type="submit"], #quoteModal input[type="submit"]');
    if (!submitBtn) return;

    trackAnalyticsEvent('quote_form_submit_click', {
      modal_id: 'quoteModal',
      button_text: (submitBtn.value || submitBtn.textContent || '').trim(),
    });
  });

  const quoteModalEl = document.getElementById('quoteModal');
  if (quoteModalEl) {
    quoteModalEl.addEventListener('hidden.bs.modal', () => {
      if (!window.quoteFormSubmittedSuccessfully) return;

      trackAnalyticsEvent('quote_modal_closed_after_success', {
        modal_id: 'quoteModal',
      });

      window.quoteFormSubmittedSuccessfully = false;
    });
  }

  /* ==========================================================================
   Quote forms: validation, reCAPTCHA, submit (universal)
   ========================================================================== */

  // Ищем формы с отложенной invisible reCAPTCHA и нужными полями.
  const quoteForms = Array.from(document.querySelectorAll('form')).filter((form) =>
    form.querySelector('[data-recaptcha-submit]') &&
    form.querySelector('[name="firstName"]') &&
    form.querySelector('[name="lastName"]') &&
    form.querySelector('[name="email"]') &&
    form.querySelector('[name="phone"]')
  );

  quoteForms.forEach(setupQuoteForm);

  function setupQuoteForm(form) {
    if (form.dataset.quoteFormInitialized === 'true') return;
    form.dataset.quoteFormInitialized = 'true';
    form.dataset.isSubmitting = 'false';
    form.dataset.isVerifying = 'false';
    form.dataset.leadTracked = 'false';
    form.setAttribute('novalidate', 'novalidate');

    const getField = (name) => form.querySelector(`[name="${name}"]`);

    const fields = {
      firstName: getField('firstName'),
      lastName:  getField('lastName'),
      email:     getField('email'),
      phone:     getField('phone'),
    };

    const messages = {
      firstName: 'Please enter your first name.',
      lastName:  'Please enter your last name.',
      email:     'Please enter a valid email.',
      phone:     'Please enter a valid phone number.',
    };

    const validators = {
      firstName: (v) => v.trim().length > 0,
      lastName:  (v) => v.trim().length > 0,
      email:     (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim()),
      phone:     (v) => {
        const normalized = v.trim();
        const digitCount = normalized.replace(/\D/g, '').length;
        return /^[+0-9\s().-]+$/.test(normalized) && digitCount >= 7 && digitCount <= 15;
      },
    };

    const recaptchaBtn = form.querySelector('[data-recaptcha-submit]');
    const formStatus = form.querySelector('[data-form-status]');

    const delay = (milliseconds) => new Promise((resolve) => {
      window.setTimeout(resolve, milliseconds);
    });

    const clearRecaptchaRetryTimer = () => {
      if (form._recaptchaRetryTimer) {
        window.clearTimeout(form._recaptchaRetryTimer);
        form._recaptchaRetryTimer = null;
      }
      form._recaptchaRetryPending = false;
    };

    const clearVerificationTimer = () => {
      if (form._recaptchaVerificationTimer) {
        window.clearTimeout(form._recaptchaVerificationTimer);
        form._recaptchaVerificationTimer = null;
      }
      clearRecaptchaRetryTimer();
    };

    const setFormStatus = (message = '', type = 'info') => {
      if (!formStatus) return;
      formStatus.textContent = message;
      formStatus.hidden = !message;
      formStatus.classList.toggle('text-danger', type === 'error');
      formStatus.classList.toggle('text-success', type === 'success');
      formStatus.setAttribute('role', type === 'error' ? 'alert' : 'status');
    };

    const setSubmitButtonState = (state) => {
      if (!recaptchaBtn) return;
      if (!recaptchaBtn.dataset.originalText) {
        recaptchaBtn.dataset.originalText = recaptchaBtn.textContent;
      }

      const busy = state === 'verifying' || state === 'submitting';
      recaptchaBtn.disabled = busy;
      recaptchaBtn.classList.toggle('disabled', busy);
      recaptchaBtn.textContent = state === 'verifying'
        ? 'Processing...'
        : (state === 'submitting' ? 'Sending...' : recaptchaBtn.dataset.originalText);
    };

    const resetRecaptchaWidget = () => {
      const tokenEl = form.querySelector('[name="g-recaptcha-response"]');
      if (tokenEl) tokenEl.value = '';
      if (window.grecaptcha && typeof window.grecaptcha.reset === 'function'
        && typeof form._recaptchaWidgetId === 'number') {
        try {
          window.grecaptcha.reset(form._recaptchaWidgetId);
        } catch (error) {
          console.warn('Unable to reset the quote form reCAPTCHA widget.', error);
        }
      }
    };

    const resetRecaptcha = () => {
      clearVerificationTimer();
      form._recaptchaExecutionAttempts = 0;
      resetRecaptchaWidget();
    };

    const restoreAfterVerificationFailure = (message, error) => {
      clearVerificationTimer();
      form.dataset.isVerifying = 'false';
      form.dataset.isSubmitting = 'false';
      if (window.currentSubmittingForm === form) window.currentSubmittingForm = null;
      resetRecaptcha();
      setSubmitButtonState('idle');
      setFormStatus(message, 'error');
      if (error) console.error('Quote form verification failed.', error);
    };

    form._setQuoteFormStatus = setFormStatus;
    form._setQuoteButtonState = setSubmitButtonState;
    form._resetQuoteRecaptcha = resetRecaptcha;
    form._clearQuoteVerificationTimer = clearVerificationTimer;

    const ensureRecaptchaWidget = () => {
      if (typeof form._recaptchaWidgetId === 'number'
        && window.grecaptcha
        && typeof window.grecaptcha.execute === 'function') {
        return Promise.resolve(window.grecaptcha);
      }

      if (!form._recaptchaInitPromise) {
        form._recaptchaInitPromise = loadRecaptchaApi()
          .then((grecaptcha) => {
            const container = form.querySelector('.recaptcha-container');
            const sitekey = recaptchaBtn ? recaptchaBtn.dataset.sitekey : '';
            if (!container || !sitekey || typeof grecaptcha.render !== 'function') {
              throw new Error('reCAPTCHA container, site key, or API is unavailable');
            }

            if (typeof form._recaptchaWidgetId !== 'number') {
              form._recaptchaWidgetId = grecaptcha.render(container, {
                sitekey,
                size: 'invisible',
                badge: 'bottomright',
                callback: window.onSubmit,
                'error-callback': () => form.dispatchEvent(new CustomEvent('recaptcha-error')),
                'expired-callback': () => form.dispatchEvent(new CustomEvent('recaptcha-expired')),
              });
              form._recaptchaRenderedAt = Date.now();
            }

            return grecaptcha;
          })
          .catch((error) => {
            form._recaptchaInitPromise = null;
            throw error;
          });
      }

      return form._recaptchaInitPromise;
    };

    form._prepareQuoteRecaptcha = () => ensureRecaptchaWidget();

    function getFeedbackEl(input) {
      if (!input) return null;
      const parent =
        input.closest('.mb-3, .form-group, .col, .col-md-6, .col-md-12') ||
        input.parentElement;
      return parent ? parent.querySelector('.invalid-feedback') : null;
    }

    function setFieldState(input, ok, msg) {
      if (!input) return;
      input.classList.toggle('is-invalid', !ok);
      input.classList.toggle('is-valid', ok);
      const fb = getFeedbackEl(input);
      if (fb && !ok) fb.textContent = msg || '';
    }

    function validateField(name) {
      const input = fields[name];
      if (!input) return true;
      const val = input.value || '';
      const ok = validators[name] ? validators[name](val) : true;
      setFieldState(input, ok, messages[name]);
      return ok;
    }

    function validateForm() {
      const order = ['firstName', 'lastName', 'email', 'phone'];
      let allOk = true;
      let firstBad = null;

      for (const key of order) {
        const ok = validateField(key);
        if (!ok && !firstBad) firstBad = fields[key];
        allOk = allOk && ok;
      }

      if (!allOk && firstBad) {
        setFormStatus('Please review the highlighted fields and try again.', 'error');
        firstBad.focus({ preventScroll: true });
        // Для инлайновой формы — скроллим к полю
        if (!form.closest('.modal')) {
          const rect = firstBad.getBoundingClientRect();
          const headerOff = getHeaderOffset();
          const top = rect.top + window.pageYOffset - headerOff - 20;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }

      if (allOk) setFormStatus('');

      return allOk;
    }

    function formValuesAreReady() {
      return Object.keys(fields).every((key) => {
        const input = fields[key];
        return !input || !validators[key] || validators[key](input.value || '');
      });
    }

    function waitForFormValues() {
      const activeElement = document.activeElement;
      if (activeElement && form.contains(activeElement) && typeof activeElement.blur === 'function') {
        activeElement.blur();
      }

      return delay(FORM_VALUE_SETTLE_DELAY).then(() => {
        if (formValuesAreReady()) return true;
        return delay(FORM_VALUE_RETRY_DELAY).then(formValuesAreReady);
      });
    }

    const handleRecaptchaFailure = (error) => {
      if (form.dataset.isVerifying !== 'true') return;
      if (form._recaptchaRetryPending) return;

      const attempts = Number(form._recaptchaExecutionAttempts || 0);
      if (attempts <= RECAPTCHA_MAX_RETRIES) {
        form._recaptchaRetryPending = true;
        setFormStatus('Processing your request...');
        resetRecaptchaWidget();
        console.warn(`Retrying quote form verification (${attempts}/${RECAPTCHA_MAX_RETRIES}).`, error);

        form._recaptchaRetryTimer = window.setTimeout(() => {
          form._recaptchaRetryTimer = null;
          form._recaptchaRetryPending = false;
          executeRecaptcha();
        }, RECAPTCHA_RETRY_DELAY * Math.max(1, attempts));
        return;
      }

      restoreAfterVerificationFailure(
        'Verification could not be completed. Please tap Send to try again.',
        error
      );
    };

    const executeRecaptcha = () => {
      form._recaptchaExecutionAttempts = Number(form._recaptchaExecutionAttempts || 0) + 1;

      ensureRecaptchaWidget()
        .then((grecaptcha) => {
          if (form.dataset.isVerifying !== 'true' || window.currentSubmittingForm !== form) {
            return;
          }

          const elapsedSinceRender = Date.now() - Number(form._recaptchaRenderedAt || 0);
          const settleDelay = Math.max(0, RECAPTCHA_WIDGET_SETTLE_DELAY - elapsedSinceRender);

          return delay(settleDelay).then(() => {
            if (form.dataset.isVerifying !== 'true' || window.currentSubmittingForm !== form) {
              return;
            }

            const execution = grecaptcha.execute(form._recaptchaWidgetId);
            if (execution && typeof execution.catch === 'function') {
              execution.catch(handleRecaptchaFailure);
            }
          });
        })
        .catch(handleRecaptchaFailure);
    };

    // live-валидация
    Object.keys(fields).forEach((key) => {
      const el = fields[key];
      if (!el) return;
      el.addEventListener('input', () => validateField(key));
      el.addEventListener('blur', () => validateField(key));
    });

    // One semantic submit path works for pointer taps, keyboard submit, and iOS autofill.
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (form.dataset.isSubmitting === 'true' || form.dataset.isVerifying === 'true') return;

      window.currentSubmittingForm = form;
      form.dataset.isVerifying = 'true';
      form._recaptchaExecutionAttempts = 0;
      setSubmitButtonState('verifying');
      setFormStatus('Processing your request...');

      // Start loading immediately while iOS finishes committing AutoFill/input values.
      ensureRecaptchaWidget().catch(() => {});

      waitForFormValues().then(() => {
        if (form.dataset.isVerifying !== 'true') return;
        if (!validateForm()) {
          form.dataset.isVerifying = 'false';
          if (window.currentSubmittingForm === form) window.currentSubmittingForm = null;
          setSubmitButtonState('idle');
          return;
        }

        clearVerificationTimer();
        form._recaptchaVerificationTimer = window.setTimeout(() => {
          restoreAfterVerificationFailure(
            'Verification took too long. Please tap Send to try again.',
            new Error('reCAPTCHA verification timed out')
          );
        }, RECAPTCHA_VERIFICATION_TIMEOUT);

        executeRecaptcha();
      });
    });

    form.addEventListener('recaptcha-error', () => {
      handleRecaptchaFailure(new Error('reCAPTCHA returned an error callback'));
    });

    form.addEventListener('recaptcha-expired', () => {
      handleRecaptchaFailure(new Error('reCAPTCHA token expired'));
    });

    // 3) после успешной капчи (вызывается из window.onSubmit)
    form.addEventListener('recaptcha-verified', () => {
      clearVerificationTimer();
      form.dataset.isVerifying = 'false';
      if (!validateForm()) {
        if (window.currentSubmittingForm === form) window.currentSubmittingForm = null;
        setSubmitButtonState('idle');
        resetRecaptcha();
        return;
      }
      submitQuoteForm(form);
    });

    // Prepare the external API while the visitor is filling the form. This avoids
    // starting the entire reCAPTCHA loading chain from the iOS submit tap.
    form.addEventListener('focusin', () => {
      ensureRecaptchaWidget().catch((error) => {
        console.warn('Unable to preload the quote form reCAPTCHA widget.', error);
      });
    }, { once: true });
  }

  if (quoteModalEl) {
    quoteModalEl.addEventListener('show.bs.modal', () => {
      const form = quoteModalEl.querySelector('form');
      if (!form || typeof form._prepareQuoteRecaptcha !== 'function') return;
      form._prepareQuoteRecaptcha().catch((error) => {
        console.warn('Unable to prepare reCAPTCHA when the quote modal opened.', error);
      });
    });
  }

  function trackLeadEvent(form) {
    if (form.dataset.leadTracked === 'true') return;
    form.dataset.leadTracked = 'true';

    trackAnalyticsEvent('generate_lead', {
      form_name: 'estimate_modal',
      form_source: form.dataset.formSource || getQuoteFormSource(null),
      page_path: window.location.pathname,
    });

    if (
      window.obCookieConsent &&
      window.obCookieConsent.hasConsent('marketing') &&
      typeof window.fbq === 'function'
    ) {
      window.fbq('track', 'Lead', {
        form_name: 'estimate_modal',
        form_source: form.dataset.formSource || getQuoteFormSource(null),
      });
    }
  }

  function submitQuoteForm(form) {
    if (form.dataset.isSubmitting === 'true') return;

    const inModal  = !!form.closest('.modal');
    const modal    = inModal ? form.closest('.modal') : null;
    window.quoteFormSubmittedSuccessfully = false;
    const modalBody = modal ? modal.querySelector('.modal-body') : null;
    const setFormStatus = form._setQuoteFormStatus || (() => {});
    const setButtonState = form._setQuoteButtonState || (() => {});
    const resetRecaptcha = form._resetQuoteRecaptcha || (() => {});

    const setSubmitting = (isSubmitting) => {
      form.dataset.isSubmitting = isSubmitting ? 'true' : 'false';
      setButtonState(isSubmitting ? 'submitting' : 'idle');
    };

    const resetFormAfterSuccess = () => {
      form.reset();
      form.classList.remove('was-validated');
      form.querySelectorAll('.is-valid, .is-invalid').forEach((el) => {
        el.classList.remove('is-valid', 'is-invalid');
      });
      resetRecaptcha();
      form.dataset.leadTracked = 'false';
      form.dataset.isVerifying = 'false';
      if (window.currentSubmittingForm === form) window.currentSubmittingForm = null;
      setFormStatus('');
      setSubmitting(false);
    };

    setSubmitting(true);

    const action = '/send_form.php';
    const fd = new FormData(form);
    if (fd.get('company')) {
      setSubmitting(false);
      return;
    }

    if (!fd.has('pagePath')) {
      fd.append('pagePath', window.location.pathname);
    }
    if (!fd.has('formContext')) {
      fd.append('formContext', inModal ? 'modal' : 'inline');
    }

    fetch(action, { method: 'POST', body: fd, credentials: 'same-origin' })
      .then(async (res) => {
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = { status: 'error' };
        }
        const ok = res.ok && data && data.status === 'success';
        if (ok) {
          window.quoteFormSubmittedSuccessfully = true;
          trackLeadEvent(form);
        }

        if (modalBody) {
          if (ok) {
            form.style.display = 'none';
            const status = document.createElement('div');
            status.className = 'text-center py-4';
            status.setAttribute('role', 'status');
            status.innerHTML = '<h5 class="mb-2">Sent</h5><p class="mb-3">Thank you! We will contact you shortly.</p><button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>';
            modalBody.appendChild(status);
            setSubmitting(false);

            modal.addEventListener(
              'hidden.bs.modal',
              () => {
                if (status && status.parentNode) status.parentNode.removeChild(status);
                form.style.display = '';
                resetFormAfterSuccess();
              },
              { once: true }
            );
          } else {
            setFormStatus('The form could not be sent. Please try again.', 'error');
            setSubmitting(false);
            resetRecaptcha();
          }
        } else {
          // Инлайн-форма
          if (ok) {
            form.innerHTML =
              '<div class="alert alert-success p-5 text-center"><h4>Thank you!</h4><p>Your request has been sent. We will contact you shortly.</p></div>';
          } else {
            setFormStatus('The form could not be sent. Please try again later.', 'error');
            setSubmitting(false);
            resetRecaptcha();
          }
        }
      })
      .catch((error) => {
        console.error('Quote form request failed.', error);
        form.style.display = '';
        setFormStatus('The form could not be sent. Check your connection and try again.', 'error');
        setSubmitting(false);
        resetRecaptcha();
      })
      .finally(() => {
        if (window.currentSubmittingForm === form) window.currentSubmittingForm = null;
      });
  }

  /* ==========================================================================
     Process Nav & Other Page Logic
     ========================================================================== */

  /* ==========================================================================
   Process Nav & Other Page Logic
   ========================================================================== */

    const processRoot = document.querySelector('.process');
    const processContent = processRoot ? processRoot.querySelector('.process__content') : null;

    const processSections = processRoot
      ? Array.from(processRoot.querySelectorAll('.process__content-step'))
      : [];

    const processTitles = processRoot
      ? Array.from(processRoot.querySelectorAll('.process__nav-title'))
      : [];

    const processLinks = processRoot
      ? Array.from(processRoot.querySelectorAll('.process__nav-item'))
      : [];

    // If the content area is scrollable, use it as scroll container
    const getScrollContainer = () => {
      if (!processContent) return null;
      return processContent.scrollHeight > processContent.clientHeight + 1 ? processContent : null;
    };

    function setActiveBySection(section) {
      // keep backward-compat (title.active) + also set link.active
      processTitles.forEach(t => t.classList.remove('active'));
      processLinks.forEach(l => l.classList.remove('active'));

      if (!section) return;

      const id = `#${section.id}`;
      const link = processRoot ? processRoot.querySelector(`.process__nav-item[href="${id}"]`) : null;

      const title = link ? link.closest('.process__nav-title') : null;
      if (title) title.classList.add('active');
      if (link) link.classList.add('active');
    }

    function getTargetTopInContainer(container, target) {
      // target position inside container's scroll coordinate system
      const cRect = container.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();
      return (tRect.top - cRect.top) + container.scrollTop;
    }

    function scrollContainerToTarget(container, target, behavior = 'smooth') {
      const top = Math.max(0, Math.round(getTargetTopInContainer(container, target)));
      container.scrollTo({ top, behavior });
    }

    function getCurrentSectionInContainer(container) {
      // “top line” inside the container viewport
      const marker = container.scrollTop + 1;

      let current = null;
      for (const sec of processSections) {
        const secTop = getTargetTopInContainer(container, sec);
        if (secTop <= marker) current = sec;
      }
      return current;
    }

    function getCurrentSectionInPage() {
      const marker = window.pageYOffset + getHeaderOffset() + 1;
      let current = null;
      for (const sec of processSections) {
        if (sec.offsetTop <= marker) current = sec;
      }
      return current;
    }

    // Click nav: set active immediately, then scroll container OR page
    processLinks.forEach((navItem) => {
      navItem.addEventListener('click', (event) => {
        event.preventDefault();

        const href = navItem.getAttribute('href');
        if (!href || href === '#') return;

        const target = processRoot ? processRoot.querySelector(href) : document.querySelector(href);
        if (!target) return;

        // 1) instantly activate clicked section
        setActiveBySection(target);

        // 2) scroll right surface
        const container = getScrollContainer();
        if (container) {
          scrollContainerToTarget(container, target, 'smooth');
        } else {
          scrollToWithOffset(target, 'smooth');
        }

        // 3) keep URL clean (same as you had)
        if (history.replaceState) {
          history.replaceState(null, '', location.pathname + location.search);
        }
      });
    });

    // Bind scroll-sync depending on mode (container vs page)
    function bindProcessScrollSync() {
      const container = getScrollContainer();

      // cleanup old handlers if rebinding
      if (processContent && processContent._processScrollHandler) {
        processContent.removeEventListener('scroll', processContent._processScrollHandler);
        processContent._processScrollHandler = null;
      }
      if (window._processWindowScrollHandler) {
        window.removeEventListener('scroll', window._processWindowScrollHandler);
        window._processWindowScrollHandler = null;
      }

      if (container) {
        const onScroll = () => {
          setActiveBySection(getCurrentSectionInContainer(container));
        };
        container.addEventListener('scroll', onScroll, { passive: true });
        processContent._processScrollHandler = onScroll;

        // init state
        setActiveBySection(getCurrentSectionInContainer(container));
      } else {
        const onWinScroll = () => {
          setActiveBySection(getCurrentSectionInPage());
        };
        window.addEventListener('scroll', onWinScroll, { passive: true });
        window._processWindowScrollHandler = onWinScroll;

        // init state
        setActiveBySection(getCurrentSectionInPage());
      }
    }

    bindProcessScrollSync();
    window.addEventListener('resize', debounceGlobal(bindProcessScrollSync, 150));
    window.addEventListener('orientationchange', bindProcessScrollSync);

  /* ==========================================================================
     LightGallery (per-project carousels)
     ========================================================================== */

  const galleryElements = Array.from(document.querySelectorAll('.js-lg-project-gallery'));
  if (galleryElements.length) {
    let galleryLoadPromise = null;
    let globalSidebar = null;

    const bindProjectLightGalleries = () => {
      const lgFactory = window.lightGallery;
      if (typeof lgFactory !== 'function') {
        throw new Error('LightGallery did not expose its browser API');
      }

      galleryElements.forEach((galleryEl) => {
        if (galleryEl.dataset.lgBound === '1') return;

        const title = galleryEl.dataset.title || '';
        const description = galleryEl.dataset.description || '';
        const tags = galleryEl.dataset.tags
          ? galleryEl.dataset.tags.split('|')
          : [];
        const lgInstance = lgFactory(galleryEl, {
          selector: 'a.js-lg-item',
          download: false,
          counter: false,
          thumbnail: false,
        });
        galleryEl._lightGalleryInstance = lgInstance;
        const lgId = lgInstance.lgId;
        const getMediaContainerPosition = lgInstance.getMediaContainerPosition.bind(lgInstance);

        lgInstance.getMediaContainerPosition = () => {
          const position = getMediaContainerPosition();

          return {
            top: position.top,
            bottom: position.top,
          };
        };

        galleryEl.addEventListener('lgAfterOpen', () => {
          const lgContent = document.getElementById(`lg-content-${lgId}`);
          if (!lgContent) return;

          if (globalSidebar && globalSidebar.parentNode) {
            globalSidebar.parentNode.removeChild(globalSidebar);
          }
          if (!globalSidebar) {
            globalSidebar = document.createElement('aside');
            globalSidebar.className = 'lg-sidebar';
          }

          globalSidebar.innerHTML = `
            ${title ? `<h3>${title}</h3>` : ''}
            ${description ? `<div class="lg-desc">${description}</div>` : ''}
            ${tags.length ? `
              <div class="tags">
                <ul class="tags__list">
                  ${tags.map((tag) => `<li class="tags__item">${tag}</li>`).join('')}
                </ul>
              </div>
            ` : ''}
          `;
          lgContent.appendChild(globalSidebar);
        });

        galleryEl.addEventListener('lgAfterClose', () => {
          if (globalSidebar && globalSidebar.parentNode) {
            globalSidebar.parentNode.removeChild(globalSidebar);
          }
        });

        galleryEl.dataset.lgBound = '1';
      });
    };

    const prepareProjectGalleries = () => {
      if (!galleryLoadPromise) {
        loadStylesheetOnce(
          '/assets/vendor/css/lightgallery-bundle.min.css',
          'lightgallery-styles'
        );
        galleryLoadPromise = loadScriptOnce(
          '/assets/vendor/js/lightgallery.min.js',
          'lightgallery-script'
        ).then(bindProjectLightGalleries);
      }
      return galleryLoadPromise;
    };

    if ('IntersectionObserver' in window) {
      const galleryObserver = new IntersectionObserver((entries, observer) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          prepareProjectGalleries().catch(() => {});
          observer.disconnect();
        }
      }, { rootMargin: '500px 0px' });
      galleryObserver.observe(galleryElements[0]);
    }

    galleryElements.forEach((galleryEl) => {
      const ensureBeforeOpen = (event) => {
        if (galleryEl.dataset.lgBound === '1') return;
        const link = event.target.closest('a.js-lg-item');
        if (!link) return;
        const targetIndex = Array.from(galleryEl.querySelectorAll('a.js-lg-item')).indexOf(link);
        event.preventDefault();
        prepareProjectGalleries()
          .then(() => {
            const instance = galleryEl._lightGalleryInstance;
            if (galleryEl.dataset.lgBound !== '1' || !instance) {
              throw new Error('Project gallery was not bound');
            }
            instance.openGallery(Math.max(0, targetIndex));
          })
          .catch((error) => {
            console.error('Project gallery could not be initialized.', error);
          });
      };
      galleryEl.addEventListener('click', ensureBeforeOpen);
      galleryEl.addEventListener('pointerenter', () => {
        prepareProjectGalleries().catch(() => {});
      }, { once: true, passive: true });
      galleryEl.addEventListener('focusin', () => {
        prepareProjectGalleries().catch(() => {});
      }, { once: true });
    });
  }

});
